import { useState, useCallback } from 'react';
import { supabase } from '../supabase';
import { getUserFacingErrorMessage } from '../utils/sanitizeErrorForLog';

export const useImageUpload = (onImagesAdded) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');

  const uploadImages = useCallback(async (files, currentImageCount = 0) => {
    if (files.length === 0) return;

    const maxImages = 4;
    if (currentImageCount + files.length > maxImages) {
      setError(`You can only upload up to ${maxImages} images.`);
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('You must be signed in to upload images.');

      const newImages = [];
      
      for (const file of files) {
        // Validate file
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
          setError(`Please select a valid image file (JPEG, PNG, GIF, or WebP).`);
          continue;
        }

        if (file.size > 10 * 1024 * 1024) {
          setError(`File size must be less than 10MB.`);
          continue;
        }

        // Upload file
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(2, 15);
        const fileExtension = file.name.split('.').pop();
        const fileName = `post-${user.id}-${timestamp}-${randomId}.${fileExtension}`;

        // Upload with optimized caching (30 days = 2592000 seconds)
        // Images don't change once uploaded, so long cache duration reduces egress
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('post-images')
          .upload(fileName, file, { cacheControl: '2592000', upsert: false }); // 30 days

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('post-images')
          .getPublicUrl(fileName);

        newImages.push({
          id: `temp-${Date.now()}-${Math.random()}`,
          image_url: publicUrl,
          file_name: fileName,
          file_size: file.size,
          mime_type: file.type,
          sort_order: currentImageCount + newImages.length
        });
      }

      if (newImages.length > 0) {
        onImagesAdded(newImages);
        setError('');
      }

    } catch (error) {
      console.error('Upload error:', error);
      setError(getUserFacingErrorMessage(error, 'Failed to upload images. Please try again.'));
    } finally {
      setIsUploading(false);
    }
  }, [onImagesAdded]);

  const clearError = useCallback(() => {
    setError('');
  }, []);

  return {
    isUploading,
    error,
    uploadImages,
    clearError
  };
};
